import { SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { Dispatch, JSX, SetStateAction } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IGracePeriod, IProduct } from '@feathr/blackbox';
import { NumberInput, Select } from '@feathr/components';
import PackageDetailsCard from '@feathr/extender/App/Settings/GracePeriodSelect/PackageDetailsCard';
import { useAccount, useFlags } from '@feathr/extender/state';

import UpdatedLicenseDate from './UpdatedGracePeriodCard';

export interface IGracePeriodSelectProps {
  onChange: Dispatch<SetStateAction<IProduct | undefined>>;
  product?: IProduct;
}

function GracePeriodSelect({ onChange, product }: Readonly<IGracePeriodSelectProps>): JSX.Element {
  const account = useAccount();
  const { csmManager: hasAccountManagerFlag } = useFlags();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { add_ons: addOns, discounts, packages, services } = account.get('license');

  const handleInputChange = (newValue: number, key: keyof IGracePeriod): void => {
    if (product) {
      onChange({
        ...product,
        grace_period: {
          ...product.grace_period,
          [key]: newValue,
        },
      });
    }
  };

  function handlePackageChange(newValue: IProduct): void {
    onChange(newValue);
  }

  function handleChangeStartDate(newValue?: number): void {
    handleInputChange(newValue ?? 0, 'start');
  }

  function handleChangeEndDate(newValue?: number): void {
    handleInputChange(newValue ?? 0, 'end');
  }

  function handleSelectOptionValue(pkg: IProduct): string {
    return pkg.name;
  }

  function handleSelectClear(): void {
    onChange(undefined);
  }

  const selectOptions = [...addOns, ...discounts, ...packages, ...services].filter(
    (item: IProduct) => item.opportunity_line_item_id,
  );

  const noAvailablePackages = selectOptions.length === 0;

  return (
    <>
      <Select<IProduct>
        // Managing package grace period is reserved for Account Managers (previously known as CSM).
        disabled={!hasAccountManagerFlag || noAvailablePackages}
        getOptionValue={handleSelectOptionValue}
        helpText={t(
          'As an account manager, add a grace period to a package on the account. Contact support if the desired package is not listed.',
        )}
        isClearable={true}
        label={t('Packages')}
        name={'grace-period-select'}
        onChange={handlePackageChange}
        onClear={handleSelectClear}
        options={selectOptions}
        placeholder={
          noAvailablePackages
            ? t('No available packages on this account...')
            : t('Select package on the account...')
        }
        value={product}
      />
      {product && (
        <PackageDetailsCard name={product.name} opp_id={product.opp_id} period={product.period} />
      )}
      {product && (
        <SimpleGrid cols={2} data-name={'grace-period-fieldset'} spacing={theme.spacing['4']}>
          <Stack gap={theme.spacing['2']}>
            <NumberInput
              disabled={!hasAccountManagerFlag || !product}
              helpText={t('Add a grace period to the start of the license.')}
              label={t('Start earlier')}
              min={0}
              onChange={handleChangeStartDate}
              suffix={t('day', { count: product?.grace_period?.start ?? 0 })}
              value={product?.grace_period?.start}
            />
            {product && (
              <UpdatedLicenseDate
                gracePeriod={product.grace_period}
                gracePeriodKey={'start'}
                period={product.period}
              />
            )}
          </Stack>
          <Stack gap={theme.spacing['2']}>
            <NumberInput
              disabled={!hasAccountManagerFlag || !product}
              helpText={t('Add a grace period to the end of the license.')}
              label={t('End later')}
              min={0}
              onChange={handleChangeEndDate}
              suffix={t('day', { count: product?.grace_period?.end ?? 0 })}
              value={product?.grace_period?.end}
            />
            {product && (
              <UpdatedLicenseDate
                gracePeriod={product.grace_period}
                gracePeriodKey={'end'}
                period={product.period}
              />
            )}
          </Stack>
        </SimpleGrid>
      )}
    </>
  );
}

export default observer(GracePeriodSelect);
